import type { ComponentProps } from 'react';
import type { Sheet } from '@/components/ui/sheet';
import { useAgentPanel } from '@/context/agent-panel.context';
import { useAgentContainer } from '@/context/agent-container.context';
import { useAgentPanelEnabled } from '@/components/common/useAgentPanelEnabled';
import { useNamiPanelLayout } from './useNamiPanelLayout';

type SheetOpenChange = NonNullable<
  ComponentProps<typeof Sheet>['onOpenChange']
>;
type SheetOpenChangeDetails = Parameters<SheetOpenChange>[1];

function isAssistantElement(node: EventTarget | null | undefined): boolean {
  return (
    node instanceof Element && node.closest('[data-capture-exclude]') !== null
  );
}

/**
 * Whether a would-be close came from the assistant (panel, pill, chat
 * container are all marked `data-capture-exclude`). A focus move is judged by
 * where focus went (`relatedTarget`), a click or key by its target.
 */
export function isFromAssistant(event: Event | undefined): boolean {
  if (event instanceof FocusEvent && isAssistantElement(event.relatedTarget)) {
    return true;
  }
  return isAssistantElement(event?.target);
}

/**
 * Lets a full-screen slide-in (`SheetContentResizable`) share the screen with
 * the docked Nami panel instead of covering it.
 *
 * Slide-ins sit at z-1300 and are modal, so on their own they hide the panel
 * (z-50) and make it inert. When the panel can dock here, the sheet:
 * - stops `--agent-panel-width` short of the right edge (content and
 *   backdrop), so the panel sits beside it;
 * - is non-modal, so the panel takes clicks and typing;
 * - stays open when a click, Escape or focus move comes from the assistant.
 *   Anything else closes it as before.
 *
 * On narrow viewports the panel overlays the page instead of docking, so the
 * sheet keeps its usual modal behaviour and `canDock` is false.
 */
export function useNamiSheetDock(onOpenChange: (open: boolean) => void) {
  const { isOpen, toggle } = useAgentPanel();
  const { effectiveModuleConfig } = useAgentContainer();
  const enabled = useAgentPanelEnabled(effectiveModuleConfig);
  const layout = useNamiPanelLayout();
  const canDock =
    enabled && effectiveModuleConfig !== null && layout === 'docked';

  const handleOpenChange = (open: boolean, details: SheetOpenChangeDetails) => {
    if (!open && canDock && isFromAssistant(details.event)) {
      details.cancel();
      return;
    }
    onOpenChange(open);
  };

  return {
    canDock,
    panelOpen: canDock && isOpen,
    toggle: () => toggle(),
    sheetProps: { modal: !canDock, onOpenChange: handleOpenChange },
    contentClassName: canDock
      ? 'right-[var(--agent-panel-width,0px)] w-[calc(100%-var(--agent-panel-width,0px))]'
      : undefined,
    overlayClassName: canDock
      ? 'right-[var(--agent-panel-width,0px)]'
      : undefined,
  };
}
